import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useUser } from '../contexts/UserContext';
import { Col, Container, Row, Dropdown } from "react-bootstrap";
import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import DataApi from "../api/dataApi";
import DashboardApi from "../api/dashboardApi";
import ScrollToTop from "./common/ScrollToTop";
import './Dashboard.css'; 
import '../App.css';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  navy: "#f0f4ff", ink: "#d4dbf0", slate: "#8b96b0", muted: "#5a6480",
  border: "rgba(255,255,255,0.08)", surface: "rgba(255,255,255,0.04)",
  white: "#ffffff", blue: "#3b82f6", blueSoft: "rgba(59,130,246,0.12)",
  cyan: "#06b6d4", emerald: "#10b981", emeraldSoft: "rgba(16,185,129,0.1)",
  amber: "#f59e0b", amberSoft: "rgba(245,158,11,0.1)",
  rose: "#f43f5e", roseSoft: "rgba(244,63,94,0.1)",
  violet: "#8b5cf6", violetSoft: "rgba(139,92,246,0.1)",
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt = (n) => (n == null || isNaN(n) ? "0" : Number(n).toLocaleString("en-IN"));
const fmtDate = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const initials = (name = "") => name.split(" ").slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");
const greeting = (h) => h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";

// ─── SMALL COMPONENTS ────────────────────────────────────────────────────────
const SecLabel = ({ icon, children }) => <div className="ld-sec"><span>{icon}</span>{children}</div>;
const MetricCard = ({ title, value, sub, icon, accent, accentSoft, onClick }) => (
  <div className="ld-metric" style={{ "--ac": accent, "--acs": accentSoft }} onClick={onClick}>
    <div className="ld-metric-ic"><i className={`fa-solid ${icon}`} /></div>
    <div className="ld-metric-lbl">{title}</div>
    <div className="ld-metric-val">{value}</div>
    {sub && <div className="ld-metric-sub">{sub}</div>}
  </div>
);
const Chip = ({ label, accent, accentSoft }) => (
  <span className="ld-chip" style={{ "--ac": accent, "--acs": accentSoft }}>
    <span style={{ width: 5, height: 5, borderRadius: "50%", background: accent, display: "inline-block" }} />
    {label}
  </span>
);
const Panel = ({ title, sub, badge, children, footer, onClick, style }) => (
  <div className="ld-panel" style={style} onClick={onClick}>
    <div className="ld-panel-hd"><div><div className="ld-panel-ttl">{title}</div>{sub && <div className="ld-panel-sub">{sub}</div>}</div>{badge}</div>
    <div className="ld-panel-body">{children}</div>
    {footer && <div className="ld-panel-foot">{footer}</div>}
  </div>
);

// ─── DASHBOARD COMPONENT ──────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const { userInfo, refreshUserInfo } = useUser();
  
  const [localUser, setLocalUser] = useState(userInfo || {});
  const [loading, setLoading] = useState(true);
  const [liveTime, setLiveTime] = useState(new Date());
  const [upcomingDueMembers, setUpcomingDueMembers] = useState([]);
  const [expiringMembers, setExpiringMembers] = useState([]);
  const [latestMembers, setLatestMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [topBooks, setTopBooks] = useState([]);
  const [metrics, setMetrics] = useState({
    dueSoonCount: 0, overdueCount: 0, damagedCount: 0,
    totalTitles: 0, totalBooks: 0, availableBooks: 0,
    issuedBooks: 0, total_copies: 0, lostCount: 0,
  });

  // 1. Live Clock
  useEffect(() => { const t = setInterval(() => setLiveTime(new Date()), 1000); return () => clearInterval(t); }, []);

  // Sync user info
  useEffect(() => { if (userInfo && !localUser.id) setLocalUser(userInfo); }, [userInfo, localUser.id]);

  // 2. CONSOLIDATED MASTER FETCH (Prevents Race Conditions)
  const fetchData = useCallback(async () => {
    try {
      const ts = Date.now(); // Cache buster

      // Run all API calls concurrently
      const [
        summaryR, alertsR, booksR, activeIssuesR, allIssuesR, membersR, subsR
      ] = await Promise.allSettled([
        new DataApi("library").get(`/dashboard?t=${ts}`),
        DashboardApi.fetchAll(`?t=${ts}`),
        DashboardApi.fetchBooks(`?t=${ts}`),
        new DataApi("bookissue").get(`/active?t=${ts}`),
        new DataApi("bookissue").fetchAll(`?t=${ts}`),
        new DataApi("librarycard").fetchAll(`?t=${ts}`),
        new DataApi("subscriptions").fetchAll(`expiring=true&t=${ts}`)
      ]);

      // --- A. Process Summary Data ---
      const sData = summaryR.status === "fulfilled" && summaryR.value.data?.success ? summaryR.value.data.data : {};
      const sumM = sData.summary || {};
      const catData = sData.booksByCategory || [];

      // --- B. Process Alert Data ---
      const alertsD = alertsR.status === "fulfilled" ? (alertsR.value.data || {}) : {};

      // --- C. Process Active Issues (CRITICAL FOR PIE CHART) ---
      let activeIssuedCount = 0;
      if (activeIssuesR.status === "fulfilled") {
        const aData = activeIssuesR.value.data;
        const activeList = aData?.data || aData?.rows || aData || [];
        activeIssuedCount = Array.isArray(activeList) ? activeList.length : 0;
      }

      // --- D. Set Unified Metrics ---
      setMetrics({
        totalTitles: Number(sumM.totalTitles || sumM.total_books || 0),
        totalBooks: Number(sumM.totalBooks || sumM.total_copies || 0),
        availableBooks: Number(sumM.availableBooks || sumM.available_copies || 0),
        issuedBooks: activeIssuedCount, // Forced sync with actual active issues
        damagedCount: Number(sumM.dammegedbooks || alertsD.damaged_missing_books || 0),
        lostCount: Number(sumM.lostBooks || 0),
        total_copies: Number(sumM.totalCopies || sumM.total_copies || 0),
        dueSoonCount: Number(alertsD.total_due_soon || 0),
        overdueCount: Number(alertsD.overdue_books || 0)
      });

      // --- E. Process Books ---
      if (booksR.status === "fulfilled") {
        const bList = booksR.value.data || [];
        setTopBooks(bList.map(b => ({
          title: b.title || "Unknown",
          available_copies: Number(b.available_count || 0),
          issued_copies: Number(b.borrowed_count || 0),
          damaged_copies: Number(b.damaged_count || 0),
          lost_copies: Number(b.lost_copies || 0)
        })).sort((a, b) => b.available_copies - a.available_copies).slice(0, 10));
      }

      // --- F. Process Categories ---
      if (catData.length) {
        setCategories(catData.slice(0, 5).map(i => ({ name: i.category_name || "Unknown", count: parseInt(i.book_count || 0) })));
      }

      // --- G. Process Upcoming Due Dates ---
      if (allIssuesR.status === "fulfilled") {
        const issues = Array.isArray(allIssuesR.value.data) ? allIssuesR.value.data : (allIssuesR.value.data?.rows || []);
        const now = new Date(); now.setHours(0, 0, 0, 0);
        const limit = new Date(now); limit.setDate(limit.getDate() + 3);
        const map = new Map();
        
        issues.filter(i => {
          if (!i.due_date) return false;
          const d = new Date(i.due_date); d.setHours(0, 0, 0, 0);
          return d >= now && d <= limit && (i.status === "issued" || i.status === "active");
        }).forEach(i => {
          const mid = i.member_id || i.card_id || i.memberId;
          if (!map.has(mid)) map.set(mid, { id: mid, name: i.member_name || i.user_name || `Member ${mid}`, due_date: fmtDate(new Date(i.due_date)), due_count: 1 });
          else map.get(mid).due_count++;
        });
        setUpcomingDueMembers(Array.from(map.values()).sort((a, b) => new Date(a.due_date) - new Date(b.due_date)).slice(0, 5));
      }

      // --- H. Process Latest Members ---
      if (membersR.status === "fulfilled") {
        const ms = Array.isArray(membersR.value.data) ? membersR.value.data : (membersR.value.data?.rows || []);
        setLatestMembers(ms.map(m => ({
          id: m.id || m.member_id,
          name: `${m.firstname || m.first_name || m.name || ""} ${m.lastname || m.last_name || ""}`.trim() || `Member ${m.id}`,
          created_at: m.created_at || m.createdAt,
          card_number: m.card_number || `CARD${m.id}`,
          status: m.status || "active",
        })).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5));
      }

      // --- I. Process Expiring Subs ---
      if (subsR.status === "fulfilled") {
        const subs = subsR.value.data?.data || subsR.value.data || [];
        const now = new Date();
        const limit = new Date(now); limit.setDate(limit.getDate() + 1); limit.setHours(23, 59, 59, 999);
        const map = new Map();

        subs.filter(s => {
          if (!s.end_date) return false;
          const ed = new Date(s.end_date); ed.setHours(0, 0, 0, 0);
          return ed >= now && ed <= limit;
        }).forEach(s => {
          const id = s.card_id || s.id;
          if (!map.has(id)) map.set(id, {
            id, name: s.member_name || `${s.firstname || ""} ${s.lastname || ""}`.trim() || "Unknown",
            card_number: s.card_number || `CARD${id}`, expiry_date: s.end_date ? fmtDate(new Date(s.end_date)) : "N/A",
            plan_name: s.plan_name || "Standard", daysLeft: Math.ceil((new Date(s.end_date) - now) / 86400000),
          });
        });
        setExpiringMembers(Array.from(map.values()).slice(0, 10));
      }

    } catch (e) {
      console.error("Dashboard Sync Error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // 3. Mount & Auto-Refresh Listeners
  useEffect(() => {
    fetchData(); // Initial load
    const handleFocus = () => fetchData();
    const handleVisibility = () => document.visibilityState === 'visible' && fetchData();
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchData]);

  // 4. Listeners for Profile/Plan changes via PubSub
  useEffect(() => {
    if (window.PubSub) {
      const userSub = window.PubSub.subscribe('USER_UPDATED', (msg, data) => {
        if (data?.user) { setLocalUser(data.user); refreshUserInfo?.(data.user); }
      });
      const planSub = window.PubSub.subscribe('PLAN_UPDATED', () => fetchData());
      return () => { window.PubSub.unsubscribe(userSub); window.PubSub.unsubscribe(planSub); };
    }
  }, [fetchData, refreshUserInfo]);

  // ─── CHART CONFIGURATIONS ────────────────────────────────────────────────────
  const barOpts = {
    chart: { type: "bar", stacked: true, fontFamily: "DM Sans,sans-serif", toolbar: { show: false }, background: "transparent" },
    plotOptions: { bar: { borderRadius: 5, columnWidth: "55%" } },
    colors: [C.blue, C.cyan, C.rose, C.slate],
    xaxis: { categories: topBooks.map(b => b.title.length > 14 ? b.title.slice(0, 14) + "…" : b.title), labels: { style: { colors: C.muted, fontSize: "10px" } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { colors: C.muted, fontSize: "10px" } } },
    grid: { borderColor: "rgba(255,255,255,0.05)", strokeDashArray: 4 },
    legend: { position: "top", fontSize: "11px", markers: { radius: 3 }, labels: { colors: C.slate } },
    dataLabels: { enabled: false },
    tooltip: { shared: true, intersect: false, theme: "dark" },
    theme: { mode: "dark" },
  };

  const barSeries = [
    { name: "Available", data: topBooks.map(b => b.available_copies) },
    { name: "Issued", data: topBooks.map(b => b.issued_copies) },
    { name: "Damaged", data: topBooks.map(b => b.damaged_copies) },
    { name: "Lost", data: topBooks.map(b => b.lost_copies) },
  ];

  // Dynamically calculate Donut array directly from unified metrics
  const donutSeriesData = useMemo(() => [
    metrics.availableBooks, 
    metrics.issuedBooks, 
    metrics.overdueCount, 
    metrics.damagedCount, 
    metrics.lostCount
  ], [metrics]);

  const pieOpts = {
    chart: { type: "donut", fontFamily: "DM Sans,sans-serif", background: "transparent" },
    labels: ["Available", "Issued", "Overdue", "Damaged", "Lost"],
    colors: [C.cyan, C.blue, C.rose, C.amber, C.slate],
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: { pie: { donut: { size: "62%", labels: { show: true, total: { show: true, label: "Total", fontSize: "12px", color: "black", formatter: (w) => fmt(w.globals.seriesTotals.reduce((a, b) => a + b, 0)) }, value: { color: "black", fontSize: "18px", fontWeight: "700", fontFamily: "JetBrains Mono,monospace" } } } } },
    theme: { mode: "dark" },
    tooltip: { theme: "dark" },
  };

  const DL = ["Available", "Issued", "Overdue", "Damaged", "Lost"];
  const DC = [C.cyan, C.blue, C.rose, C.amber, C.slate];

  const dueColor = (s) => {
    const diff = Math.ceil((new Date(s).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
    return diff <= 0 ? { ac: C.rose, acs: C.roseSoft } : diff === 1 ? { ac: C.amber, acs: C.amberSoft } : { ac: C.blue, acs: C.blueSoft };
  };

  const CARDS = [
    { title: "Book Titles", value: fmt(metrics.totalTitles || metrics.totalBooks), icon: "fa-book", accent: C.blue, accentSoft: C.blueSoft, sub: <><i className="fa-solid fa-layer-group" style={{ fontSize: 10 }} /> {fmt(metrics.total_copies)} total copies</>, type: "totalBooks" },
    { title: "Available Copies", value: fmt(metrics.availableBooks), icon: "fa-circle-check", accent: C.emerald, accentSoft: C.emeraldSoft, sub: <span style={{ color: C.rose }}><i className="fa-solid fa-minus" style={{ fontSize: 10 }} /> {fmt(metrics.issuedBooks)} in use</span>, type: "copiesStatus" },
    { title: "Due Soon", value: fmt(metrics.dueSoonCount), icon: "fa-clock", accent: C.amber, accentSoft: C.amberSoft, type: "dueSoon" },
    { title: "Overdue", value: fmt(metrics.overdueCount), icon: "fa-triangle-exclamation", accent: C.rose, accentSoft: C.roseSoft, sub: <span style={{ color: C.rose }}>Requires attention</span>, type: "overdue" },
  ];

  const goCard = (type) => type === "dueSoon" ? navigate("/booksubmit?filter=due_soon") : type === "overdue" ? navigate("/booksubmit?filter=overdue") : navigate("/book");

  if (loading) return (
    <div className="ld-loader">
      <div className="ld-spinner" />
      <div style={{ fontSize: 13, color: "var(--c-text-2)", fontWeight: 500, letterSpacing: .5 }}>Loading dashboard…</div>
    </div>
  );

  const displayName = `${localUser?.firstname || localUser?.first_name || ""} ${localUser?.lastname || localUser?.last_name || ""}`.trim() || localUser?.username || localUser?.email?.split('@')[0] || "Admin";

  return (
    <div className="ld">
      <ScrollToTop />
      {/* ══ HEADER ══════════════════════════════════════════════════════════════ */}
      <header className="ld-hdr">
        <div className="ld-hdr-left">
          <span className="ld-hdr-greet">{greeting(liveTime.getHours())}</span>
          <div className="ld-hdr-name">{displayName} 👋</div>
        </div>

        <div className="ld-hdr-right">
          
          <Dropdown.Toggle as="button" className="ld-exp-btn border-0" id="ld-exp-dd">
            <span className="ld-exp-dot" />
            <i className="fa-solid fa-user-clock" style={{ color: C.amber, fontSize: 14 }} /> Plan Expiries
            {expiringMembers.length > 0 && <span className="ld-exp-badge">{expiringMembers.length}</span>}
          </Dropdown.Toggle>

          <Dropdown.Menu className="ld-exp-menu border-0 p-0 shadow-none">
            <div className="ld-exp-mhead d-flex align-items-center justify-content-between px-3 py-1 border-bottom">
              <span className="ld-exp-mhead-lbl"><i className="fa-solid fa-triangle-exclamation" style={{ color: C.amber, fontSize: 11 }} /> Expiring soon</span>
              <span className="ld-exp-mcount">{expiringMembers.length} total</span>
            </div>
            <div className="ld-exp-scroll">
              {expiringMembers.length > 0 ? expiringMembers.map((m, i) => (
                <div key={i} className="ld-exp-row" onClick={() => navigate(`/librarycard/${m.id}`)}>
                  <div className="ld-exp-av">{m.name.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ld-exp-name">{m.name}</div>
                    <div className="ld-exp-meta"><i className="fa-regular fa-calendar-xmark" style={{ fontSize: 9, marginRight: 4 }} /> Expires {m.expiry_date}</div>
                  </div>
                  <div className="ld-exp-plan">{m.plan_name}</div>
                </div>
              )) : (
                <div className="ld-exp-empty"><i className="fa-solid fa-shield-check" style={{ fontSize: 26, color: C.emerald, opacity: .5 }} /><span>All plans up to date</span></div>
              )}
            </div>
          </Dropdown.Menu>

          <div className="ld-div" />
          <div className="ld-clock-wrap">
            <div className="ld-clock">{liveTime.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}</div>
            <div className="ld-date">{liveTime.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</div>
            <div className="ld-live"><span className="ld-live-dot" /> Live</div>
          </div>
        </div>
      </header>

      {/* ══ BODY ════════════════════════════════════════════════════════════════ */}
      <Container fluid className="py-4 px-3 px-md-4" style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* KPI */}
        <Row className="g-3 mb-2">
          {CARDS.map((c, i) => <Col xl={3} lg={6} md={6} sm={12} key={i}><MetricCard {...c} onClick={() => goCard(c.type)} /></Col>)}
        </Row>

        <SecLabel icon="⚡">Urgent Actions</SecLabel>
        <Row className="g-3 mb-2">
          <Col lg={4}>
            <Panel title="Upcoming Due Dates" sub="Members due in next 48 hours" badge={<Chip label={`${upcomingDueMembers.length} DUE`} accent={C.amber} accentSoft={C.amberSoft} />} footer={<><i className="fa-solid fa-arrow-right me-1" />View all due books</>} onClick={() => navigate("/booksubmit?filter=due_soon")} style={{ cursor: "pointer" }}>
              {upcomingDueMembers.length > 0 ? upcomingDueMembers.slice(0, 5).map(m => {
                const { ac, acs } = dueColor(m.due_date);
                return (
                  <div key={m.id} className="ld-row" onClick={e => { e.stopPropagation(); navigate(`/librarycard/${m.id}`); }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div className="ld-av" style={{ "--ac": ac, "--acs": acs }}>{initials(m.name) || "?"}</div>
                      <div>
                        <div className="ld-row-name">{m.name}</div>
                        <div className="ld-row-meta"><i className="fa-solid fa-calendar-days" style={{ fontSize: 10 }} /> Due <strong style={{ color: ac }}>{m.due_date}</strong> · {m.due_count} book{m.due_count !== 1 ? "s" : ""}</div>
                      </div>
                    </div>
                    <Chip label="Due Soon" accent={ac} accentSoft={acs} />
                  </div>
                );
              }) : <div className="ld-empty"><i className="fa-solid fa-calendar-check" style={{ fontSize: 22, color: C.emerald }} /><span>No upcoming due dates</span></div>}
            </Panel>
          </Col>

          <Col lg={8}>
            <Panel title="Books by Availability" sub="Top 10 titles — available, issued, damaged & lost" style={{ height: '100%' }}>
              <div style={{ padding: "4px 16px 8px" }}>
                {topBooks.length > 0
                  ? <Chart key={`bar-chart-${topBooks.map(b => b.issued_copies).join('-')}`} options={barOpts} series={barSeries} type="bar" height={290} />
                  : <div className="ld-empty"><i className="fa-solid fa-chart-column" style={{ fontSize: 28 }} /><span>No inventory data</span></div>}
              </div>
            </Panel>
          </Col>
        </Row>

        <SecLabel icon="📈">Analytics</SecLabel>
        <Row className="g-3">
          <Col lg={3} md={6}>
            <Panel title="Top Categories" sub="Most populated by stock" onClick={() => navigate("/classification")} badge={<button onClick={e => { e.stopPropagation(); navigate("/classification"); }} style={{ background: C.blueSoft, border: "1px solid rgba(59,130,246,0.2)", color: "#60a5fa", borderRadius: 8, padding: "4px 12px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "DM Sans,sans-serif" }}>VIEW ALL</button>}>
              {categories.length > 0 ? categories.map((cat, i) => {
                const ac = [C.blue, C.emerald, C.violet, C.amber, C.cyan][i % 5];
                return (
                  <div key={i} className="ld-row">
                    <div style={{ display: "flex", alignItems: "center" }}><div className="ld-cat-dot" style={{ "--ac": ac, background: ac }} /><div className="ld-cat-name">{cat.name}</div></div>
                    <span className="ld-cat-cnt">{fmt(cat.count)}</span>
                  </div>
                );
              }) : <div className="ld-empty"><i className="fa-solid fa-tags" style={{ fontSize: 22 }} /><span>No category data</span></div>}
            </Panel>
          </Col>

          <Col lg={6} md={6}>
            <Panel title="Copies Status" sub="Distribution across all statuses">
              <div style={{ paddingTop: 8 }}>
                {/* Dynamically tracking Issued & Available to force ApexChart refresh */}
                <Chart key={`donut-${metrics.issuedBooks}-${metrics.availableBooks}`} options={pieOpts} series={donutSeriesData} type="donut" height={220} />
              </div>
              <div className="ld-dstats">
                {DL.map((lbl, i) => (
                  <div key={lbl} className="ld-dstat">
                    <div className="ld-dval">{fmt(donutSeriesData[i] || 0)}</div>
                    <div className="ld-dlbl"><span className="ld-dsw" style={{ background: DC[i] }} />{lbl}</div>
                  </div>
                ))}
              </div>
            </Panel>
          </Col>

          <Col lg={3} md={12}>
            <Panel title="Latest Members" sub="Recently joined library cards" onClick={() => navigate("/librarycard")} footer={<><i className="fa-solid fa-users me-1" />Manage members</>} badge={<button onClick={e => { e.stopPropagation(); navigate("/librarycard"); }} style={{ background: C.blueSoft, border: "1px solid rgba(59,130,246,0.2)", color: "#60a5fa", borderRadius: 8, padding: "4px 12px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "DM Sans,sans-serif" }}>VIEW ALL</button>} style={{ cursor: "pointer" }}>
              {latestMembers.length > 0 ? latestMembers.map(m => (
                <div key={m.id} className="ld-row" onClick={e => { e.stopPropagation(); navigate(`/librarycard/${m.id}`); }}>
                  <div style={{ display: "flex", alignItems: "center" }}><div className="ld-av" style={{ "--ac": C.blue, "--acs": C.blueSoft }}>{initials(m.name) || "?"}</div><div><div className="ld-row-name">{m.name}</div><div className="ld-row-meta"><i className="fa-solid fa-id-card" style={{ fontSize: 9 }} /> {m.card_number}</div></div></div>
                  <Chip label={m.status || "active"} accent={m.status === "active" ? C.emerald : C.muted} accentSoft={m.status === "active" ? C.emeraldSoft : "rgba(255,255,255,0.06)"} />
                </div>
              )) : <div className="ld-empty"><i className="fa-solid fa-users" style={{ fontSize: 22 }} /><span>No members found</span></div>}
            </Panel>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;